import { PortableText } from '@portabletext/react';
import { GatsbyImage } from 'gatsby-plugin-image';
import PropTypes from 'prop-types';
import React, { useRef } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { ChevronLeft, ChevronRight } from 'react-feather';
import MultiCarousel from 'react-multi-carousel';
import imgFallback from 'src/utils/image';
import SectionHeader from '../SectionHeader/SectionHeader';
import { headerWithDescType } from 'src/types';
import { classNames } from 'src/utils/helpers';

const responsive = {
  desktop: {
    breakpoint: { max: 9999, min: 1024 },
    items: 1,
  },
  tablet: {
    breakpoint: { max: 1024, min: 464 },
    items: 1,
  },
  mobile: {
    breakpoint: { max: 464, min: 0 },
    items: 1,
  },
};

function ComplexCarousel({ hasCta = false, isDark = false, sectionsWithImageAndLink, sectionHeader: { title, tagline, description } }) {
  const ref = useRef();
  const handleNext = () => ref.current.next();
  const handlePrevious = () => ref.current.previous();

  return (
    <Container
      fluid
      as="section"
      className={classNames('py-5', {
        'dark-mode bg-gray-800 py-7': isDark,
      })}
    >
      <SectionHeader title={title} tagline={tagline} description={description} isDark={isDark} />
      <Container fluid>
        <Row>
          <Col xs={12} className="mt-5 px-0">
            <MultiCarousel
              responsive={responsive}
              infinite
              pauseOnHover
              itemClass="slide-item"
              draggable
              swipeable
              keyBoardControl
              ref={ref}
              showDots={false}
              arrows={false}
              centerMode
            >
              {sectionsWithImageAndLink &&
                sectionsWithImageAndLink.map((item) => (
                  <Col key={item._key} xs={12} className="flicker-bugfix px-0">
                    <Col className="carousel-image-container">
                      <GatsbyImage
                        image={item?.image?.image?.asset?.gatsbyImageData ?? imgFallback}
                        alt={item?.image?.alt ?? ''}
                        className="fade-in-lottie"
                      />
                    </Col>
                    <Col
                      className={classNames('font-weight-bolder pt-2', {
                        'text-white': isDark,
                      })}
                    >
                      {item.title}
                    </Col>
                    <Col
                      className={classNames('rfs-size-sm pt-2', {
                        'text-gray-650': isDark,
                        'text-muted': !isDark,
                      })}
                    >
                      <PortableText value={item.description} />
                    </Col>
                    {item?.links &&
                      item.links.map((link) => (
                        <a
                          key={link._key}
                          href={link.url}
                          className={classNames('m-2', {
                            'btn-link-secondary': isDark,
                            'btn-link-primary': !isDark,
                          })}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {link.title}
                        </a>
                      ))}
                  </Col>
                ))}
            </MultiCarousel>
          </Col>
          <Col xs={12} md={{ span: 6, offset: 3 }} className="mt-3">
            <Row className="justify-content-between align-items-center">
              <Col xs="auto">
                {hasCta && (
                  <a
                    href="/kapcsolat"
                    className={classNames('btn', {
                      'btn-link-secondary': isDark,
                      'btn-link-primary': !isDark,
                    })}
                  >
                    Kérj ajánlatot
                  </a>
                )}
              </Col>
              <Col xs="auto">
                <Row className="justify-content-center align-items-center">
                  <Col xs="auto">
                    <button
                      type="button"
                      onClick={handlePrevious}
                      className={classNames('arrow d-flex rounded-circle border-0 p-3 transition-colors', {
                        'text-secondary bg-gray-800': isDark,
                        'text-primary bg-white': !isDark,
                      })}
                      style={{
                        outlineColor: `var(${isDark ? '--secondary' : '--primary'})`,
                      }}
                    >
                      <ChevronLeft size={20} />
                    </button>
                  </Col>
                  <Col xs="auto">
                    <button
                      type="button"
                      onClick={handleNext}
                      className={classNames('arrow d-flex rounded-circle border-0 p-3 transition-colors', {
                        'text-secondary bg-gray-800': isDark,
                        'text-primary bg-white': !isDark,
                      })}
                      style={{
                        outlineColor: `var(${isDark ? '--secondary' : '--primary'})`,
                      }}
                    >
                      <ChevronRight size={20} />
                    </button>
                  </Col>
                </Row>
              </Col>
            </Row>
          </Col>
        </Row>
      </Container>
    </Container>
  );
}

ComplexCarousel.propTypes = {
  isDark: PropTypes.bool,
  hasCta: PropTypes.bool,
  sectionsWithImageAndLink: PropTypes.arrayOf(
    PropTypes.shape({
      _key: PropTypes.string,
      title: PropTypes.string,
      image: PropTypes.shape({}),
      description: PropTypes.arrayOf(PropTypes.shape({})),
      links: PropTypes.arrayOf(
        PropTypes.shape({
          _key: PropTypes.string,
          url: PropTypes.string,
          title: PropTypes.string,
        })
      ),
    })
  ).isRequired,
  sectionHeader: headerWithDescType.propTypes,
};

ComplexCarousel.defaultProps = {
  isDark: false,
  hasCta: false,
  sectionHeader: headerWithDescType.defaultProps,
};

export default ComplexCarousel;
